
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Magnet, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const formSchema = z.object({
  name: z.string().min(2, {
    message: "Le nom doit comporter au moins 2 caractères.",
  }),
  email: z.string().email({
    message: "Veuillez saisir une adresse email valide.",
  }),
  phone: z.string().min(10, {
    message: "Veuillez saisir un numéro de téléphone valide.",
  }),
  metalType: z.string({
    required_error: "Veuillez sélectionner un type de métal.",
  }),
  quantity: z.string().min(1, {
    message: "Veuillez indiquer une estimation de quantité.",
  }),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const MetalEstimationForm = () => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      metalType: "",
      quantity: "",
      description: "",
    },
  });

  async function onSubmit(data: FormValues) {
    setIsSubmitting(true);
    
    try {
      console.log("Envoi de la demande d'estimation:", data);
      
      const { data: response, error } = await supabase.functions.invoke('send-metal-estimation', {
        body: data
      });

      if (error) {
        console.error("Erreur lors de l'envoi:", error);
        toast.error("Erreur lors de l'envoi", {
          description: "Une erreur est survenue lors de l'envoi de votre demande. Veuillez réessayer.",
        });
        return;
      }

      console.log("Réponse reçue:", response);
      
      toast.success("Demande envoyée avec succès !", {
        description: "Nous avons bien reçu votre demande d'estimation. Notre équipe vous contactera rapidement.",
      });
      
      form.reset();
    } catch (error) {
      console.error("Erreur lors de l'envoi:", error);
      toast.error("Erreur lors de l'envoi", {
        description: "Une erreur est survenue lors de l'envoi de votre demande. Veuillez réessayer.",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-md">
      <div className="flex items-center mb-6">
        <Magnet className="h-6 w-6 text-fabre-green mr-2" />
        <h2 className="text-2xl font-heading font-bold text-fabre-gray">
          Estimation de vos métaux
        </h2>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom et prénom</FormLabel>
                  <FormControl>
                    <Input placeholder="Votre nom complet" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="votre@email.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Téléphone</FormLabel>
                  <FormControl>
                    <Input placeholder="Votre numéro de téléphone" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="metalType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type de métal</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez un type de métal" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="ferraille">Ferraille</SelectItem>
                      <SelectItem value="aluminium">Aluminium</SelectItem>
                      <SelectItem value="cuivre">Cuivre</SelectItem>
                      <SelectItem value="inox">Inox</SelectItem>
                      <SelectItem value="laiton">Laiton</SelectItem>
                      <SelectItem value="zinc">Zinc</SelectItem>
                      <SelectItem value="plomb">Plomb</SelectItem>
                      <SelectItem value="fonte">Fonte</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="quantity"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Quantité estimée (kg)</FormLabel>
                <FormControl>
                  <Input placeholder="Ex: 100 kg" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description (optionnel)</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Décrivez en détail les métaux que vous souhaitez vendre (état, origine, etc.)"
                    className="resize-none h-[100px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-fabre-green hover:bg-fabre-darkgreen transition-colors flex items-center justify-center"
          >
            {isSubmitting ? (
              "Envoi en cours..."
            ) : (
              <>
                Envoyer la demande
                <Send className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
          <p className="text-sm text-gray-600 text-center">
            Nous vous contacterons rapidement pour vous proposer une estimation personnalisée.
          </p>
        </form>
      </Form>
    </div>
  );
};

export default MetalEstimationForm;
